'use client';

import { Check, Plus } from 'lucide-react';

import React, { useEffect, useState } from 'react';

import Image from 'next/image';
import { usePathname, useSearchParams } from 'next/navigation';
import { useSilentRouter } from '@shared/hooks/use-router';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

import { ScreenLayout, UserOnboarding } from '@/features/user-onboarding/components';
import { OnboardingScreen } from '@/features/user-onboarding/constants/user-onboarding-constants';
import { Workspace } from '@/features/user-settings/types/workspace-types';

import { useAppCallbacks, useAppStore } from '../contexts/app-wrapper/providers/app-provider';
import Avatar from './avatar';

type WorkspaceChooseScreenProps = {
	className?: string;
};

export default function WorkspaceChooseScreen({}: WorkspaceChooseScreenProps) {
	const router = useSilentRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();

	const { workspaces } = useAppStore((state) => state);
	const { handleWorkspaceSelectionComplete } = useAppCallbacks((state) => state);

	const [showWorkspaceCreate, setShowWorkspaceCreate] = useState(false);
	const hasWorkspaces = workspaces && workspaces.length > 0;

	useEffect(() => {
		if (searchParams.get('view') === 'create-workspace') setShowWorkspaceCreate(true);
		else setShowWorkspaceCreate(false);
	}, [searchParams]);

	// Case 1: No workspaces present - show onboarding with only workspace screen
	if (!hasWorkspaces || showWorkspaceCreate) {
		return (
			<UserOnboarding
				source="choose-workspace"
				allowedScreens={[OnboardingScreen.WORKSPACE_DETAILS]}
				onComplete={handleWorkspaceSelectionComplete}
			/>
		);
	}

	// Case 2: Workspaces present - show workspace selection UI
	return (
		<ScreenLayout
			title="Choose a workspace"
			cardContent={
				<div className="flex w-full max-w-[400px] flex-col items-center justify-center gap-4">
					{workspaces.length > 0 ? (
						<>
							<WorkspaceList />
						</>
					) : null}

					<Button
						variant="outline"
						className="w-full"
						onClick={() => {
							router.push(`${pathname}?${searchParams.toString()}&view=create-workspace`);
						}}
					>
						<Plus
							size={16}
							className="mr-2"
						/>
						Create new workspace
					</Button>
				</div>
			}
		/>
	);
}

function WorkspaceList() {
	const { workspaces, selectedWorkspaceId } = useAppStore((state) => state);
	const { updateDefaultWorkspaceId } = useAppCallbacks((state) => state);

	const handleWorkspaceSelect = async (workspace: Workspace) => {
		await updateDefaultWorkspaceId(workspace.id);
		window.location.reload();
	};

	return (
		<div className="w-full flex-col gap-1">
			<div className="max-h-[200px] rounded-md border bg-slate-50">
				<ScrollArea
					className="max-h-[inherit] pr-2"
					viewportClassName="max-h-[inherit]"
				>
					<div className="flex flex-col gap-1 p-1">
						{workspaces.map((workspace) => (
							<button
								key={workspace.id}
								onClick={() => handleWorkspaceSelect(workspace)}
								className="flex w-full items-center justify-between gap-2 rounded-sm px-3 py-2.5 text-sm transition-colors hover:bg-slate-100 hover:text-accent-foreground"
							>
								<div className="flex items-center gap-3">
									{workspace.logo ? (
										<Image
											src={workspace.logo}
											alt={workspace.name}
											width={32}
											height={32}
											className="size-8 rounded object-cover"
										/>
									) : (
										<Avatar
											small
											name={workspace?.name}
											fallbackClassName="rounded"
											className="size-8 rounded"
										/>
									)}
									<span className="text-sm font-medium">{workspace.name}</span>
								</div>
								{selectedWorkspaceId === workspace.id && <Check className="h-4 w-4 text-primary" />}
							</button>
						))}
					</div>
				</ScrollArea>
			</div>
		</div>
	);
}
